/**
 * components/rightbar/orchSteps.ts —— 「任务进展」页 · **编排步骤聚合**数据面（★20260925）。
 *
 * 真源：`102:frontend_src/src/rightbar/orchSteps.js`（179 行）——**逐字译 JS→TS**，零行为改动。
 *
 * 依据（承源件）：
 *   · `orch_step_done` 的落点由**旧树（102）**规范表 `task_events_constants.py::EVENT_SURFACE_PLACEMENT`
 *     定为 `panel`（**仅进右栏聚合、不进对话流**——真库 566 行进流必刷屏）。⚠️ 口径标注
 *     （★PFK2R 订正，COLLECT_PFK2_s §5-1）：该规范表**新树不存在**（`find src -name
 *     task_events_constants.py` ＝ 空；仅存于「旧树抄写源」/）⇒ 此处引述为**旧树口径**；
 *     新树现状（20261012 复跑）＝`src/sononux` 内 `orch_step_done` **零生产方** ∧ 未进新树事件词域
 *     （`grep -rn orch_step src/sononux` ＝ 空）⇒ 行源缺口如实登记（消缺归事件词域批）；
 *   · 数据源＝**会话事件窗口**（单源，不另立端点、不读第二源）；本件**不是**定义件
 *     （不导出 `match/start/update/publication`、不注册进任何认领集）；
 *   · `message` 是 `code_run` 写的 JSON 文本 ⇒ **唯一 `JSON.parse` 处就在这里**（组件层零解析）；
 *     非 JSON 载荷 ⇒ **明确降级**（`degraded` 计数供 UI 留痕），不猜字段、不抛错、不丢事件；
 *   · K＝`ORCH_STEP_MAX_ROWS`（20，单一来源＝`./ProgressTabBody`，禁各写一份）：`rows`＝**最近 K 条**
 *     （从尾部切），`count`＝全量真值，截断**明说**（`truncated`）；
 *   · 升序＝事件时刻；无可用时刻者**保序排末**（不虚构时刻，A7）；零本地时钟/定时器。
 */
import {
  ORCH_STEP_MAX_ROWS,
  parseSessionTime,
  type OrchRuntime,
  type OrchRuntimeRow,
  type OrchStepRow,
  type OrchSummary,
} from "./ProgressTabBody";

/** 编排步骤事件名（**唯一字面量**；不是任何定义认领集的成员） */
export const ORCH_STEP_EVENT_TYPE = "orch_step_done";

/**
 * 事件词域边界（K41／X11 三词域；旧树规范表 `task_events_constants.py::EVENT_SURFACE_PLACEMENT`
 * 的词域面登记位——**旧树口径**，新树无该件，见件头标注）——`wf.`＝内核作业事实／`run.`＝一次执行生命周期／`trigger.`＝触发。
 * 具体事件 id 唯一来源＝**声明面**（`src/sononux/decl/` components/baseline 投影）；本表只登记
 * **前缀边界**，不枚举、不新增事件 id。`orch_step_done`（本文件唯一消费事件）不带三词域前缀，
 * 落点归 EVENT_SURFACE_PLACEMENT 规范表（`panel`，见上）。
 */
export const EVENT_WORD_DOMAINS: Readonly<Record<string, string>> = Object.freeze({
  "wf.": "内核作业事实（X11 定案）",
  "run.": "一次执行生命周期（X11 定案）",
  "trigger.": "触发（X11 定案）",
});

/** 普通对象判定（数组／null 不算；载荷必须是对象才有字段可取） */
function plainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/** 字符串归一（空串 ⇒ null：**不给值不当给值用**）；非串标量转串（后端列可能给数字） */
function strOf(v: unknown): string | null {
  if (typeof v === "string") return v === "" ? null : v;
  if (v === null || v === undefined) return null;
  return String(v);
}

/** 载荷双认读取（先 `event.data.<name>`、再顶层 `<name>`）——本模块唯一读取路径 */
function fieldOf(ev: unknown, name: string): unknown {
  const e = ev && typeof ev === "object" ? (ev as Record<string, unknown>) : null;
  const data = e && plainObject(e.data) ? (e.data as Record<string, unknown>) : {};
  if (data[name] !== undefined && data[name] !== null) return data[name];
  return e ? e[name] : undefined;
}

/** 时刻（原值；`time` 优先、退回 `ts`；皆缺 ⇒ null——**禁本地时钟**） */
function atOf(ev: unknown): string | number | null {
  const t = fieldOf(ev, "time");
  if (t !== null && t !== undefined) return t as string | number;
  const ts = fieldOf(ev, "ts");
  return ts === undefined || ts === null ? null : (ts as string | number);
}

/** 从窗口条目里取 `orch_step_done` 事件对象；非本事件 ⇒ null（本模块只做**一个**事件的投影） */
function orchEventOf(entry: unknown): Record<string, unknown> | null {
  if (!entry || typeof entry !== "object") return null;
  const e = entry as Record<string, unknown>;
  const inner = e.event;
  if (plainObject(inner)) return inner.type === ORCH_STEP_EVENT_TYPE ? inner : null;
  // ② 服务端原始条目：`{source:'task_event', data:{event:'orch_step_done', …}}`
  if (e.source === "task_event" && plainObject(e.data)) {
    return e.data.event === ORCH_STEP_EVENT_TYPE ? (e.data as Record<string, unknown>) : null;
  }
  // ③ 裸事件
  return e.type === ORCH_STEP_EVENT_TYPE ? e : null;
}

/** 聚合行（＝`OrchStepRow` ＋ 源件额外两键 `elapsedMs`/`runId`） */
export interface OrchAggRow extends OrchStepRow {
  elapsedMs: number | null;
  runId: string | null;
}

/**
 * `message` 列 → 载荷对象。**本模块唯一 `JSON.parse` 处**（组件层零解析）。降级口径：
 *   · 无 `message`（缺键／空串）⇒ **不算降级**（`hasPayload:false`，按「缺载荷」如实呈现）；
 *   · JSON 解析为**非对象**（裸标量／数组）⇒ 降级（无字段可取）；
 *   · 解析抛错（非 JSON 文本）⇒ 降级，**不猜正文**。
 */
function readPayload(raw: unknown): { payload: Record<string, unknown> | null; hasPayload: boolean; degraded: boolean } {
  const text = strOf(raw);
  if (text === null) return { payload: null, hasPayload: false, degraded: false };
  try {
    const parsed: unknown = JSON.parse(text);
    if (plainObject(parsed)) return { payload: parsed, hasPayload: true, degraded: false };
    return { payload: null, hasPayload: true, degraded: true };
  } catch {
    return { payload: null, hasPayload: true, degraded: true };
  }
}

/** 一条 `orch_step_done` ⇒ 一行展示数据（**只做取值，不做文案**——文案归组件） */
function rowOf(ev: unknown): OrchAggRow {
  const { payload, hasPayload, degraded } = readPayload(fieldOf(ev, "message"));
  const p = payload;
  return {
    at: atOf(ev),
    stepNo: p && Number.isInteger(p.step_no) ? (p.step_no as number) : null,
    tool: strOf(p && p.tool),
    ok: p && typeof p.ok === "boolean" ? (p.ok as boolean) : null,
    error: strOf(p && p.error),
    elapsedMs: p && Number.isFinite(p.elapsed_ms) ? (p.elapsed_ms as number) : null,
    runId: strOf(p && p.run_id) ?? strOf(fieldOf(ev, "run_id")),
    hasPayload,
    degraded,
  };
}

/**
 * 窗口条目序列 ⇒ 编排步骤**聚合**（不是一行一条）。
 * `count`＝编排步骤总数（**全量真值**）；`rows`＝**最近 K 条**（升序）；`truncated`＝是否被 K 截断（**明说**）；
 * `latestAt`＝最近一条可用时刻原值（无 ⇒ null）。
 */
export function projectOrchSteps(entries: ReadonlyArray<unknown> | null | undefined): OrchSummary {
  const list = Array.isArray(entries) ? entries : [];
  const rows: OrchAggRow[] = [];
  let degraded = 0;
  for (const entry of list) {
    const ev = orchEventOf(entry);
    if (!ev) continue;
    const row = rowOf(ev);
    if (row.degraded) degraded += 1;
    rows.push(row);
  }
  // 升序＝事件时刻；无可用时刻者**保序排末**（不虚构时刻——与 ProgressTabBody 同判法）
  const sorted = rows
    .map((row, index) => [row, index] as const)
    .sort((a, b) => {
      const ta = parseSessionTime(a[0].at);
      const tb = parseSessionTime(b[0].at);
      const va = Number.isNaN(ta) ? Infinity : ta;
      const vb = Number.isNaN(tb) ? Infinity : tb;
      return va - vb || a[1] - b[1];
    })
    .map(([row]) => row);

  const count = sorted.length;
  const shown = count > ORCH_STEP_MAX_ROWS ? sorted.slice(count - ORCH_STEP_MAX_ROWS) : sorted.slice();
  let latestAt: OrchSummary["latestAt"] = null;
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    if (!Number.isNaN(parseSessionTime(sorted[i].at))) {
      latestAt = sorted[i].at;
      break;
    }
  }

  return {
    count,
    rows: shown,
    degraded,
    truncated: count > ORCH_STEP_MAX_ROWS,
    maxRows: ORCH_STEP_MAX_ROWS,
    latestAt,
  };
}

/** 运行态读面产物源（`proj.combinators.runtime_state()` 返回同形：三源行＝内核读面行原值） */
export interface OrchRuntimeSource {
  waits?: ReadonlyArray<unknown> | null;
  steps?: ReadonlyArray<unknown> | null;
  jobs?: ReadonlyArray<unknown> | null;
  gaps?: ReadonlyArray<string> | null;
}

/**
 * ★PFK2R·L-PF-B5 运行态投影（纯函数；**值＝内核原值透传**，只做行形归一，禁造值、禁换算）。
 *
 * 三源（账行判据列逐字）：`wf.job`／`wf.step`／等待项。判定词面＝内核词域：
 *   · 「在等」＝等待项**开行数**（`open_waits` 内核语义，开＝在等）；
 *   · 「在跑」＝`state` 原值 `"running"` 的行数（字面属 `kernel.state.machine.JOB_STATES`，禁自造词表）。
 * 缺源口径（诚实缺席）：`waits` 为 null/undefined（＝供方未注入或读面缺席）⇒ 返回 null，
 * 由渲染层整段不渲染——**不猜、不补 0**；`steps`/`jobs` 缺席只少行，不改等待项真值。
 */
export function projectOrchRuntime(src: OrchRuntimeSource | null | undefined): OrchRuntime | null {
  if (!src || typeof src !== "object") return null;
  const s = src as OrchRuntimeSource;
  if (!Array.isArray(s.waits)) return null;
  const rows: OrchRuntimeRow[] = [];
  for (const raw of s.waits) {
    if (!plainObject(raw)) continue;
    rows.push({
      source: "wf.wait",
      ref: strOf(raw.wait_id),
      state: "open", // open_waits 读面语义：返回行恒为开（＝在等）；非行内造值
      runId: strOf(raw.run_id),
      label: strOf(raw.label),
    });
  }
  for (const [key, source] of [["steps", "wf.step"], ["jobs", "wf.job"]] as const) {
    const list = s[key];
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      if (!plainObject(raw)) continue;
      rows.push({
        source,
        ref: strOf(source === "wf.step" ? raw.step_id : raw.job_id),
        state: strOf(raw.state),
        runId: strOf(raw.run_id),
        label: null,
      });
    }
  }
  const running = rows.filter((r) => r.state === "running").length;
  return {
    running,
    waiting: s.waits.length,
    rows,
    gaps: Array.isArray(s.gaps) ? s.gaps.filter((g): g is string => typeof g === "string") : [],
  };
}
